import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PDFPreview from "./PDFPreview";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useHealthSummary } from "@/hooks/useHealthSummary";
import { getMemberInfo } from "@/hooks/useMembers";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MLoader from "@/components/ui/atoms/MLoader";
import { triggerNativeHealthSummaryShare } from "@/utils/nativeBridge";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";
import { IconDownload, IconShare3 } from "@tabler/icons-react";

const HealthSummaryViewer: React.FC = () => {
  const isDesktop = useIsDesktopPWA();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { getHealthSummaryPdfUrl } = useHealthSummary(memberId || "");
  const [noOfPages, setNoOfPages] = useState<number>(0);

  const marginTop = noOfPages > 1 ? 120 : 50;

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url: string | null = null;
    const fetchAll = async () => {
      try {
        const pdfBlobUrl = await getHealthSummaryPdfUrl();
        if (cancelled) return;
        setPdfUrl(pdfBlobUrl);
      } catch (e: any) {
        if (!cancelled)
          setError(e?.message || "Failed to load health summary PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memberId, getHealthSummaryPdfUrl]);

  const handleShare = async () => {
    if (isDesktop && pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${memberInfo.firstName}'s Health summary.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    try {
      triggerNativeHealthSummaryShare({
        memberId,
        memberName: memberInfo.firstName,
      });
    } catch (e) {
      console.error("[HealthSummaryViewer] Error triggering native share", e);
    }
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen ">
      <div className="heading-h7-bold fixed z-[99999] bg-white w-full text-center pb-[40px]">
        Health Summary
      </div>
      <div className="w-full max-w-2xl mx-auto">
        {loading && <MLoader />}
        {pdfUrl && <PDFPreview fileUrl={pdfUrl} onLoadSuccess={setNoOfPages} />}
      </div>
      <div className="dashboardEmergencyShareContainer confirm-footer">
        <MButton
          variant="filled"
          size="md"
          className="w-full"
          onClick={handleShare}
          leftIcon={
            isDesktop ? <IconDownload size={16} /> : <IconShare3 size={16} />
          }
        >
          {isDesktop ? "Download PDF" : "Share"}
        </MButton>
      </div>
    </div>
  );
};

export default HealthSummaryViewer;
